/**
 * Tableau de Délibération
 * Page complète pour le jury : liste filtrable des étudiants avec moyennes + stats + graphes + exports.
 *
 * Filtres : niveau, pôle, option, classe, semestre
 * Stats : taux réussite, taux échec, moyenne générale, distribution mentions, top/flop
 * Charts : donut mentions, bar par option, histogramme moyennes
 * Exports : Excel (xlsx), PDF (jsPDF + autotable)
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Award, Target,
  Filter, FileSpreadsheet, FileText, Search, X, Loader,
  CheckCircle2, AlertCircle, Calculator, RefreshCw, Edit3, Lock, Unlock, Moon,
  Download, ChevronDown, Shield, MoreHorizontal,
} from 'lucide-react';
import apiClient from '../../config/api.config';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  useGetResultatsSemestreQuery, useCalculerToutNiveauMutation, useRecalculerCSMutation,
  useGetEtudiantsSansResultatQuery, ResultatSemestre,
} from '../../services/resultatsApi';
import { useGetClassesQuery, useGetPolesQuery, useGetOptionsQuery } from '../../services/polesOptionsApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import StatsDeliberation from './StatsDeliberation';
import ActionRapideDeliberationModal from '../../components/Deliberation/ActionRapideDeliberationModal';
import RachatIntelligentBatchModal from '../../components/Deliberation/RachatIntelligentBatchModal';
import RapportRachatsModal from '../../components/Deliberation/RapportRachatsModal';
import ExportComparaisonRachatsModal from '../../components/Deliberation/ExportComparaisonRachatsModal';

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

// Couleurs CAMES standard pour les mentions
const COULEURS_MENTION: Record<string, string> = {
  'Excellent':   '#10b981', // green-500
  'Très Bien':   '#22c55e', // green-400
  'Bien':        '#3b82f6', // blue-500
  'Assez Bien':  '#0ea5e9', // sky-500
  'Passable':    '#f59e0b', // amber-500
  'Ajourné':     '#ef4444', // red-500
  'En Attente':  '#9ca3af', // gray-400
};

const couleurMention = (m: string | null): string => {
  if (!m) return '#9ca3af';
  return COULEURS_MENTION[m] || '#9ca3af';
};

const decisionColor = (d: string): string => {
  const D = d.toLowerCase();
  if (D.includes('admis') || D.includes('valid')) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (D.includes('rattrapage') || D.includes('redoubl')) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
  if (D.includes('ajourné') || D.includes('refus') || D.includes('exclu')) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const TableauDeliberation: React.FC = () => {
  const { data: anneeActive } = useGetAnneeActiveQuery();
  // Année de TRAVAIL = année GLOBALE sélectionnée (localStorage `annee_academique`,
  // qui pilote aussi le header X-Annee-Academique → schéma). Fix 2026-10-15 : la
  // délibération doit suivre l'année choisie (comme la saisie). Avant, tout était
  // épinglé sur l'année ACTIVE → le param `annee` ≠ le schéma d'une année archivée,
  // d'où des notes brutes vides (filtrées sur la mauvaise année) et les UE fantômes
  // non masquées (est_annee_archivee calculé sur l'année active).
  const anneeTravail = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('annee_academique');
      if (stored) return stored;
    } catch { /* localStorage indispo */ }
    return anneeActive?.libelle || '';
  }, [anneeActive]);
  const { data: poles = [] } = useGetPolesQuery({});
  const { data: options = [] } = useGetOptionsQuery({ actif: true });
  const { data: classes = [] } = useGetClassesQuery({ actif: true });

  // Filtres
  const [niveau, setNiveau] = useState('L1');
  const [semestre, setSemestre] = useState<number>(1);
  const [pole, setPole] = useState('');
  const [option, setOption] = useState('');
  const [classe, setClasse] = useState('');
  const [search, setSearch] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');
  // Type formation : 'FI_DD' (defaut, formation initiale + double diplome) | 'FI' | 'DD' | 'CS' | 'TOUS'
  const [typeFormation, setTypeFormation] = useState<'FI_DD' | 'FI' | 'DD' | 'CS' | 'TOUS'>('FI_DD');
  // Tranche de moyenne (filtre)
  const [moyenneMin, setMoyenneMin] = useState<string>('');
  const [moyenneMax, setMoyenneMax] = useState<string>('');
  // Session : '' (toutes) | 'normale' (ordinaire) | 'rattrapage'
  const [sessionFilter, setSessionFilter] = useState<'' | 'normale' | 'rattrapage'>('');
  // Section "Statistiques par classe" : repliable + focus sur une classe spécifique
  const [showStatsClasse, setShowStatsClasse] = useState(false);
  const [classeStatsFocus, setClasseStatsFocus] = useState<string>('');
  // Action rapide délibération (notes éditables + recalc)
  const [actionRapide, setActionRapide] = useState<{
    etudiantId: number; matricule: string; nom: string;
    moyenneActuelle: number | null;
  } | null>(null);
  // Rachat intelligent batch
  const [showRachatBatch, setShowRachatBatch] = useState(false);
  // Le rachat ne peut être lancé que par les administrateurs (03/10/2026)
  const roleUtilisateur = useSelector((state: any) => state.auth.user?.role);
  const estAdminRachat = roleUtilisateur === 'super_admin' || roleUtilisateur === 'admin';
  // Rapport rachats appliqués
  const [showRapportRachats, setShowRapportRachats] = useState(false);
  const [showExportComparaison, setShowExportComparaison] = useState(false);
  const [scansPortailActif, setScansPortailActif] = useState<boolean | null>(null);
  const [togglingScans, setTogglingScans] = useState(false);
  // Verrou global de saisie des notes (admin seulement)
  const [saisieNotesVerrouillee, setSaisieNotesVerrouillee] = useState<boolean | null>(null);
  const [togglingVerrouSaisie, setTogglingVerrouSaisie] = useState(false);
  // Verrous par semestre (admin) : indépendants du verrou global
  const [verrouS1, setVerrouS1] = useState<boolean | null>(null);
  const [verrouS2, setVerrouS2] = useState<boolean | null>(null);
  const [togglingSem, setTogglingSem] = useState<'S1' | 'S2' | null>(null);

  // Fix 2026-08-25 : verrous granulaires parité × session (ord/ratt) + consultation portail
  const [verrouS1Ord, setVerrouS1Ord] = useState<boolean | null>(null);
  const [verrouS1Ratt, setVerrouS1Ratt] = useState<boolean | null>(null);
  const [verrouS2Ord, setVerrouS2Ord] = useState<boolean | null>(null);
  const [verrouS2Ratt, setVerrouS2Ratt] = useState<boolean | null>(null);
  const [portailReleveS1, setPortailReleveS1] = useState<boolean | null>(null);
  const [portailReleveS2, setPortailReleveS2] = useState<boolean | null>(null);
  // Fix 2026-08-28 : masquage granulaire des notes d'examens portail (devoirs
  // restent visibles). Séparé du verrou "relevés" qui bloque tout l'accès.
  const [masquerExamensGlobal, setMasquerExamensGlobal] = useState<boolean | null>(null);
  const [masquerExamensS1, setMasquerExamensS1] = useState<boolean | null>(null);
  const [masquerExamensS2, setMasquerExamensS2] = useState<boolean | null>(null);
  // Scans portail par parité × session (fix 2026-08-25)
  const [scansS1Ord, setScansS1Ord] = useState<boolean | null>(null);
  const [scansS1Ratt, setScansS1Ratt] = useState<boolean | null>(null);
  const [scansS2Ord, setScansS2Ord] = useState<boolean | null>(null);
  const [scansS2Ratt, setScansS2Ratt] = useState<boolean | null>(null);
  const [togglingGranular, setTogglingGranular] = useState<string | null>(null);

  // Charger l'état actuel du verrou scans portail
  useEffect(() => {
    apiClient.get('/parametres/by_key/', { params: { cle: 'portail_scans_actif' } })
      .then(r => {
        const v = r.data?.valeur_convertie;
        // Si paramètre n'existe pas encore → considéré comme actif (état actuel)
        setScansPortailActif(v === false ? false : true);
      })
      .catch(() => setScansPortailActif(true)); // fallback : considéré actif
  }, []);

  // Charger l'état actuel du verrou saisie notes
  useEffect(() => {
    apiClient.get('/parametres/by_key/', { params: { cle: 'saisie_notes_verrouillee' } })
      .then(r => {
        const v = r.data?.valeur_convertie;
        setSaisieNotesVerrouillee(v === true ? true : false);
      })
      .catch(() => setSaisieNotesVerrouillee(false));
    apiClient.get('/parametres/by_key/', { params: { cle: 'saisie_notes_verrouillee_s1' } })
      .then(r => setVerrouS1(r.data?.valeur_convertie === true ? true : false))
      .catch(() => setVerrouS1(false));
    apiClient.get('/parametres/by_key/', { params: { cle: 'saisie_notes_verrouillee_s2' } })
      .then(r => setVerrouS2(r.data?.valeur_convertie === true ? true : false))
      .catch(() => setVerrouS2(false));
    // Fix 2026-08-25 : charger les 6 nouveaux verrous granulaires
    const chargerParamBool = (cle: string, setter: (v: boolean) => void) => {
      apiClient.get('/parametres/by_key/', { params: { cle } })
        .then(r => setter(r.data?.valeur_convertie === true))
        .catch(() => setter(false));
    };
    chargerParamBool('saisie_notes_verrouillee_s1_ord', setVerrouS1Ord);
    chargerParamBool('saisie_notes_verrouillee_s1_ratt', setVerrouS1Ratt);
    chargerParamBool('saisie_notes_verrouillee_s2_ord', setVerrouS2Ord);
    chargerParamBool('saisie_notes_verrouillee_s2_ratt', setVerrouS2Ratt);
    chargerParamBool('portail_releves_verrouille_s1', setPortailReleveS1);
    chargerParamBool('portail_releves_verrouille_s2', setPortailReleveS2);
    // Fix 2026-08-28 : masquage examens uniquement (devoirs restent visibles)
    chargerParamBool('portail_masquer_examens', setMasquerExamensGlobal);
    chargerParamBool('portail_masquer_examens_s1', setMasquerExamensS1);
    chargerParamBool('portail_masquer_examens_s2', setMasquerExamensS2);
    chargerParamBool('portail_scans_verrouille_s1_ord', setScansS1Ord);
    chargerParamBool('portail_scans_verrouille_s1_ratt', setScansS1Ratt);
    chargerParamBool('portail_scans_verrouille_s2_ord', setScansS2Ord);
    chargerParamBool('portail_scans_verrouille_s2_ratt', setScansS2Ratt);
  }, []);

  // Helper de toggle générique pour les 6 nouveaux verrous granulaires
  const toggleParamBool = async (
    cle: string,
    label: string,
    actuel: boolean | null,
    setter: (v: boolean) => void,
    description: string,
    tokenLoading: string,
  ) => {
    if (actuel === null) return;
    const nouveau = !actuel;
    const msg = nouveau
      ? `VERROUILLER : ${label} ?\n\nSeul le super administrateur pourra continuer.`
      : `Déverrouiller : ${label} ?`;
    if (!window.confirm(msg)) return;
    setTogglingGranular(tokenLoading);
    try {
      await apiClient.post('/parametres/set-by-key/', {
        cle,
        valeur: nouveau ? 'true' : 'false',
        type: 'boolean',
        description,
      });
      setter(nouveau);
    } catch (e: any) {
      alert('Erreur : ' + (e?.message || ''));
    } finally {
      setTogglingGranular(null);
    }
  };

  const toggleVerrouSem = async (sem: 'S1' | 'S2') => {
    const actuel = sem === 'S1' ? verrouS1 : verrouS2;
    if (actuel === null) return;
    const nouveau = !actuel;
    const label = sem === 'S1' ? 'semestre 1 (S1/S3/S5)' : 'semestre 2 (S2/S4/S6)';
    const msg = nouveau
      ? `VERROUILLER la saisie des notes pour le ${label} ?\n\nSeul le super administrateur pourra continuer. Utile pendant les délibérations.`
      : `Déverrouiller la saisie des notes pour le ${label} ?`;
    if (!window.confirm(msg)) return;
    setTogglingSem(sem);
    try {
      const cle = sem === 'S1' ? 'saisie_notes_verrouillee_s1' : 'saisie_notes_verrouillee_s2';
      await apiClient.post('/parametres/set-by-key/', {
        cle,
        valeur: nouveau ? 'true' : 'false',
        type: 'boolean',
        description: `Si true, seul le super administrateur peut saisir/modifier des notes du ${label}.`,
      });
      if (sem === 'S1') setVerrouS1(nouveau); else setVerrouS2(nouveau);
    } catch (e: any) {
      alert('Erreur : ' + (e?.message || ''));
    } finally {
      setTogglingSem(null);
    }
  };

  const toggleVerrouSaisie = async () => {
    if (saisieNotesVerrouillee === null) return;
    const nouveau = !saisieNotesVerrouillee;
    const confirmation = nouveau
      ? 'VERROUILLER la saisie des notes pour tous les agents ?\n\nSeul le super administrateur pourra continuer à saisir/modifier des notes. Les agents recevront un message d\'erreur en tentant de saisir.\n\nConfirmer ?'
      : 'Déverrouiller la saisie des notes (réautoriser tous les agents) ?';
    if (!window.confirm(confirmation)) return;
    setTogglingVerrouSaisie(true);
    try {
      await apiClient.post('/parametres/set-by-key/', {
        cle: 'saisie_notes_verrouillee',
        valeur: nouveau ? 'true' : 'false',
        type: 'boolean',
        description: 'Si true, seul le super administrateur peut saisir ou modifier des notes (devoirs et examens).',
      });
      setSaisieNotesVerrouillee(nouveau);
    } catch (e: any) {
      alert('Erreur : ' + (e?.message || ''));
    } finally {
      setTogglingVerrouSaisie(false);
    }
  };

  const toggleScansPortail = async () => {
    if (scansPortailActif === null) return;
    const nouveau = !scansPortailActif;
    const confirmation = nouveau
      ? 'Réactiver l\'accès des étudiants aux scans (copies devoirs + examens) dans leur portail ?'
      : 'VERROUILLER les scans dans le portail étudiant ?\n\nLes étudiants verront un message "Consultation temporairement indisponible" au lieu de leurs copies.\n\nConfirmer ?';
    if (!window.confirm(confirmation)) return;
    setTogglingScans(true);
    try {
      await apiClient.post('/parametres/set-by-key/', {
        cle: 'portail_scans_actif',
        valeur: nouveau ? 'true' : 'false',
        type: 'boolean',
        description: 'Si false, les étudiants voient un message d\'indisponibilité au lieu de leurs scans.',
      });
      setScansPortailActif(nouveau);
    } catch (e: any) {
      alert('Erreur lors du changement de l\'état : ' + (e?.message || ''));
    } finally {
      setTogglingScans(false);
    }
  };

  const printRef = useRef<HTMLDivElement>(null);

  // Mutation pour calculer les résultats du niveau
  const [calculerNiveau, { isLoading: isCalculating }] = useCalculerToutNiveauMutation();

  // Fix 2026-08-25 : bannière "N étudiants sans résultat calculé sur ce semestre"
  // — utile quand le tableau montre peu d'étudiants sur les semestres pairs
  // parce que leurs UE ne sont pas toutes complètes. Le bandeau s'affiche
  // uniquement s'il y a au moins un étudiant manquant.
  const { data: manquants } = useGetEtudiantsSansResultatQuery({
    semestre: semestre as 1 | 2,
    niveau: niveau || undefined,
    pole: pole || undefined,
    option: option || undefined,
    classe: classe || undefined,
    type_formation: typeFormation || undefined,
  }, { skip: !niveau });
  const [manquantsOpen, setManquantsOpen] = useState(false);
  const [recalculerCS, { isLoading: isRecalculatingCS }] = useRecalculerCSMutation();
  const [showRecalcCSModal, setShowRecalcCSModal] = useState(false);
  const [recalcCSResult, setRecalcCSResult] = useState<{
    total_etudiants_cs: number;
    semestres_calcules: number;
    classes_touchees: string[];
    erreurs: Array<{ matricule: string; message: string }>;
  } | null>(null);
  const [recalcCSError, setRecalcCSError] = useState<string | null>(null);
  const [calcMessage, setCalcMessage] = useState<string | null>(null);
  const [calcResult, setCalcResult] = useState<any | null>(null);
  const [showErrorsModal, setShowErrorsModal] = useState(false);

  const handleCalculer = async () => {
    if (!niveau) {
      alert('Sélectionnez un niveau spécifique (L1, L2, ...) avant de lancer le calcul. L\'option « Tous les niveaux » sert uniquement à la consultation des stats.');
      return;
    }
    // Le calcul porte TOUJOURS sur la parité affichée, et sur elle seule.
    // L'ancienne fenêtre proposait « 1 = parité affichée / 2 = les deux » ;
    // le choix « 2 » a recalculé des semestres impairs déjà publiés le
    // 2026-09-10 (244 moyennes modifiées, 6 validations basculées). Une
    // opération aussi lourde ne doit pas dépendre d'un chiffre tapé à la main.
    const semLabel = semestre === 1 ? 'impairs (S1/S3/S5)' : 'pairs (S2/S4/S6)';
    const typeLabel = typeFormation === 'TOUS'
      ? 'tous les types de formation (CS compris)'
      : `le type ${typeFormation === 'FI_DD' ? 'FI + DD' : typeFormation}`;
    // Fix 2026-09-17 : le calcul respecte les filtres pôle / option / classe affichés
    const filtresLabel = [
      pole ? `pôle ${pole}` : '',
      option ? `option ${option}` : '',
      classe ? `classe ${classe}` : '',
    ].filter(Boolean).join(', ');
    if (!window.confirm(
      `Recalculer les résultats du niveau ${niveau}${filtresLabel ? ` (${filtresLabel})` : ''} ?\n\n` +
      `Périmètre exact du calcul :\n` +
      `  • semestres ${semLabel}\n` +
      `  • ${typeLabel}\n` +
      (filtresLabel ? `  • uniquement : ${filtresLabel}\n` : `  • toutes les options du niveau\n`) +
      `\n` +
      `Les semestres de l'autre parité ne seront pas touchés.\n\n` +
      `Le calcul enchaîne automatiquement deux passages et supprime les ` +
      `résultats devenus obsolètes. Comptez environ une minute par niveau.`,
    )) return;
    const scopeSemestre = semestre;
    // Le calcul doit porter sur le meme perimetre que le tableau affiche.
    // Sans cela, un clic sur « Calculer » recalcule aussi les CS alors que
    // le filtre affiche FI/DD — or les CS reposent sur une autre structure d'UE.
    const scopeType = typeFormation === 'TOUS'
      ? undefined
      : (typeFormation === 'FI_DD' ? 'FI,DD' : typeFormation);
    setCalcMessage(`⏳ Calcul en cours… (semestres ${semLabel} uniquement)`);
    setCalcResult(null);
    try {
      const result: any = await calculerNiveau({
        niveau,
        ...(scopeSemestre ? { semestre: scopeSemestre } : {}),
        ...(scopeType ? { type_formation: scopeType } : {}),
        ...(pole ? { pole } : {}),
        ...(option ? { option } : {}),
        ...(classe ? { classe_code: classe } : {}),
      } as any).unwrap();
      setCalcResult(result);
      const duree = result.duree_secondes ? ` en ${result.duree_secondes}s` : '';
      const errors = result.errors?.length || 0;
      const errorsTxt = errors > 0 ? ` · ⚠ ${errors} erreur(s)` : '';
      const purges = result.resultats_perimes_supprimes
        ? ` · ${result.resultats_perimes_supprimes} résultat(s) obsolète(s) retiré(s)`
        : '';
      setCalcMessage(`✓ ${result.etudiants_traites}/${result.total_etudiants} étudiants${duree} · ${result.total_semestres} semestres · ${result.nb_compensations || 0} compensations${purges}${errorsTxt}`);
      setTimeout(() => setCalcMessage(null), 12000);
    } catch (err: any) {
      setCalcMessage(`❌ Erreur : ${err?.data?.error || err?.data?.detail || 'calcul échoué'}`);
    }
  };

  // Export Excel du tableau de délibération au format LONG (une ligne par
  // étudiant × matière, plus une feuille synthèse). Réutilise les filtres
  // actifs de la page (niveau, pole, option, classe, type_formation, semestre).
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Menus déroulants du header (uniquement 1 ouvert à la fois)
  const [openMenu, setOpenMenu] = useState<null | 'exports' | 'rachats' | 'admin'>(null);
  const handleExportExcelDetail = async () => {
    setExportError(null);
    setExportingExcel(true);
    try {
      const params: Record<string, string> = { semestre: String(semestre) };
      if (niveau) params.niveau = niveau;
      if (pole) params.pole = pole;
      if (option) params.option = option;
      if (classe) params.classe = classe;
      // Mapping vers le format backend : FI_DD → 'FI,DD', TOUS → pas de filtre
      if (typeFormation === 'FI_DD') params.type_formation = 'FI,DD';
      else if (typeFormation !== 'TOUS') params.type_formation = typeFormation;
      if (anneeTravail) params.annee_academique = anneeTravail;
      const res = await apiClient.get('/resultats-semestre/export-excel-deliberation/', {
        params, responseType: 'blob',
      });
      // Nom fichier depuis Content-Disposition si dispo
      let filename = `deliberation_S${semestre}.xlsx`;
      const cd = (res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '') as string;
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (m?.[1]) filename = m[1];
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (err: any) {
      // Si le backend renvoie un JSON d'erreur en blob → décodage
      if (err?.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const j = JSON.parse(txt);
          setExportError(j?.error || j?.detail || 'Export échoué');
        } catch {
          setExportError('Export échoué');
        }
      } else {
        setExportError(err?.data?.error || err?.data?.detail || 'Export échoué');
      }
    } finally {
      setExportingExcel(false);
    }
  };

  // Recalcul spécifique COURS DU SOIR : moyenne directement pondérée par
  // crédits matières (sans pondération UE). Aucun impact sur les étudiants
  // FI/DD — le backend filtre `type_formation='CS'` en dur.
  const handleRecalculerCS = async () => {
    setRecalcCSError(null);
    setRecalcCSResult(null);
    try {
      const body: {
        semestre?: 1 | 2; niveau?: string; classe_code?: string; option?: string;
      } = {};
      if (semestre === 1 || semestre === 2) body.semestre = semestre;
      if (niveau) body.niveau = niveau;
      if (classe) body.classe_code = classe;
      if (option) body.option = option;
      const res = await recalculerCS(body).unwrap();
      setRecalcCSResult(res);
    } catch (err: any) {
      setRecalcCSError(err?.data?.error || err?.data?.detail || 'Recalcul CS échoué');
    }
  };

  // Charger les résultats avec les filtres serveur
  const { data: resultats = [], isLoading } = useGetResultatsSemestreQuery({
    etudiant__niveau: niveau || undefined,
    etudiant__pole: pole || undefined,
    etudiant__option: option || undefined,
    etudiant__classe: classe || undefined,
    semestre: semestre,
  });

  // Filtrer côté client (search + décision + type formation)
  const resultatsFiltres = useMemo(() => {
    let list = [...resultats];
    // Type formation (par défaut : FI + DD = formation initiale au sens large)
    if (typeFormation === 'FI_DD') {
      list = list.filter(r => r.etudiant_type_formation === 'FI' || r.etudiant_type_formation === 'DD');
    } else if (typeFormation !== 'TOUS') {
      list = list.filter(r => r.etudiant_type_formation === typeFormation);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.etudiant_matricule || '').toLowerCase().includes(q) ||
        (r.etudiant_nom || '').toLowerCase().includes(q)
      );
    }
    if (decisionFilter) {
      list = list.filter(r => (r.decision || '').toLowerCase().includes(decisionFilter.toLowerCase()));
    }
    // Filtre session : 'normale' (ordinaire) | 'rattrapage'
    if (sessionFilter) {
      list = list.filter(r => (r.session_validation || '').toLowerCase() === sessionFilter);
    }
    // Filtre tranche de moyenne : min ≤ moyenne ≤ max (bornes incluses)
    const minN = moyenneMin === '' ? null : parseFloat(moyenneMin.replace(',', '.'));
    const maxN = moyenneMax === '' ? null : parseFloat(moyenneMax.replace(',', '.'));
    if ((minN !== null && !isNaN(minN)) || (maxN !== null && !isNaN(maxN))) {
      list = list.filter(r => {
        const m = parseFloat(r.moyenne_semestre || '');
        if (isNaN(m)) return false;
        if (minN !== null && !isNaN(minN) && m < minN) return false;
        if (maxN !== null && !isNaN(maxN) && m > maxN) return false;
        return true;
      });
    }
    // Trier par moyenne desc puis nom
    list.sort((a, b) => {
      const ma = parseFloat(a.moyenne_semestre || '0');
      const mb = parseFloat(b.moyenne_semestre || '0');
      if (mb !== ma) return mb - ma;
      return (a.etudiant_nom || '').localeCompare(b.etudiant_nom || '');
    });
    // Recalculer le rang local après filtrage
    return list.map((r, idx) => ({ ...r, rang_local: idx + 1 }));
  }, [resultats, search, decisionFilter, typeFormation, moyenneMin, moyenneMax, sessionFilter]);

  // Stats globales
  const stats = useMemo(() => {
    const total = resultatsFiltres.length;
    const valides = resultatsFiltres.filter(r => r.valide).length;
    const ajournes = total - valides;
    // Décomposition session : Admis Ordinaire vs Admis Rattrapage
    // session_validation = 'normale' (ordinaire) | 'rattrapage' | '' (pas encore validé)
    const validesOrdinaire = resultatsFiltres.filter(
      r => r.valide && (r.session_validation || '').toLowerCase() === 'normale'
    ).length;
    const validesRattrapage = resultatsFiltres.filter(
      r => r.valide && (r.session_validation || '').toLowerCase() === 'rattrapage'
    ).length;
    const validesAutre = valides - validesOrdinaire - validesRattrapage;
    // Décomposition par décision (libre — basée sur le champ decision)
    const decisionsBreakdown: Record<string, number> = {};
    for (const r of resultatsFiltres) {
      const k = (r.decision || '—').trim();
      decisionsBreakdown[k] = (decisionsBreakdown[k] || 0) + 1;
    }
    const moyennes = resultatsFiltres
      .map(r => parseFloat(r.moyenne_semestre || ''))
      .filter(n => !isNaN(n));
    const moyGenerale = moyennes.length > 0
      ? moyennes.reduce((s, x) => s + x, 0) / moyennes.length
      : 0;
    const sup10 = moyennes.filter(m => m >= 10).length;
    const sup12 = moyennes.filter(m => m >= 12).length;
    const sup14 = moyennes.filter(m => m >= 14).length;
    const sup16 = moyennes.filter(m => m >= 16).length;

    return {
      total,
      valides,
      validesOrdinaire,
      validesRattrapage,
      validesAutre,
      decisionsBreakdown,
      ajournes,
      moyGenerale,
      tauxReussite: total > 0 ? (valides / total) * 100 : 0,
      tauxEchec: total > 0 ? (ajournes / total) * 100 : 0,
      sup10, sup12, sup14, sup16,
    };
  }, [resultatsFiltres]);

  // Distribution par mention
  const dataMentions = useMemo(() => {
    const counts: Record<string, number> = {};
    resultatsFiltres.forEach(r => {
      const m = r.mention || 'En Attente';
      counts[m] = (counts[m] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value, color: couleurMention(name) }));
  }, [resultatsFiltres]);

  // Distribution par tranche de moyenne
  const dataTranches = useMemo(() => {
    const tranches = [
      { range: '0-5', min: 0, max: 5, count: 0 },
      { range: '5-8', min: 5, max: 8, count: 0 },
      { range: '8-10', min: 8, max: 10, count: 0 },
      { range: '10-12', min: 10, max: 12, count: 0 },
      { range: '12-14', min: 12, max: 14, count: 0 },
      { range: '14-16', min: 14, max: 16, count: 0 },
      { range: '16-20', min: 16, max: 20.01, count: 0 },
    ];
    resultatsFiltres.forEach(r => {
      const m = parseFloat(r.moyenne_semestre || '');
      if (!isNaN(m)) {
        const t = tranches.find(t => m >= t.min && m < t.max);
        if (t) t.count++;
      }
    });
    return tranches;
  }, [resultatsFiltres]);

  // Stats par classe (top → bottom par taux de réussite)
  const dataParClasse = useMemo(() => {
    const groups: Record<string, ResultatSemestre[]> = {};
    resultatsFiltres.forEach(r => {
      const c = r.etudiant_classe || '?';
      if (!groups[c]) groups[c] = [];
      groups[c].push(r);
    });
    return Object.entries(groups).map(([classe, list]) => {
      const total = list.length;
      const valides = list.filter(r => r.valide).length;
      const moyennes = list.map(r => parseFloat(r.moyenne_semestre || '')).filter(n => !isNaN(n));
      const moy = moyennes.length > 0 ? moyennes.reduce((s, x) => s + x, 0) / moyennes.length : 0;
      return {
        classe,
        total,
        valides,
        ajournes: total - valides,
        tauxReussite: total > 0 ? (valides / total) * 100 : 0,
        moyenne: moy,
      };
    }).sort((a, b) => b.tauxReussite - a.tauxReussite);
  }, [resultatsFiltres]);

  // Agrégation générique par dimension
  const aggregateBy = (key: 'etudiant_pole' | 'etudiant_niveau') => {
    const groups: Record<string, ResultatSemestre[]> = {};
    resultatsFiltres.forEach(r => {
      const v = (r[key] as string) || '—';
      if (!groups[v]) groups[v] = [];
      groups[v].push(r);
    });
    return Object.entries(groups).map(([label, list]) => {
      const total = list.length;
      const valides = list.filter(r => r.valide).length;
      const moyennes = list.map(r => parseFloat(r.moyenne_semestre || '')).filter(n => !isNaN(n));
      const moy = moyennes.length > 0 ? moyennes.reduce((s, x) => s + x, 0) / moyennes.length : 0;
      return {
        label,
        total,
        valides,
        ajournes: total - valides,
        tauxReussite: total > 0 ? (valides / total) * 100 : 0,
        moyenne: moy,
      };
    }).sort((a, b) => b.total - a.total);
  };

  const dataParPole = useMemo(() => aggregateBy('etudiant_pole'), [resultatsFiltres]);
  const dataParNiveau = useMemo(() => aggregateBy('etudiant_niveau'), [resultatsFiltres]);

  // Matrice croisée pôle × niveau
  const dataPoleNiveau = useMemo(() => {
    const polesSet = new Set<string>();
    const niveauxSet = new Set<string>();
    resultatsFiltres.forEach(r => {
      polesSet.add(r.etudiant_pole || '—');
      niveauxSet.add(r.etudiant_niveau || '—');
    });
    const poles = Array.from(polesSet).sort();
    const niveaux = Array.from(niveauxSet).sort();
    // cell[pole][niveau] = { total, valides, ajournes, taux }
    const cells: Record<string, Record<string, { total: number; valides: number; ajournes: number; taux: number; moy: number }>> = {};
    poles.forEach(p => {
      cells[p] = {};
      niveaux.forEach(n => {
        cells[p][n] = { total: 0, valides: 0, ajournes: 0, taux: 0, moy: 0 };
      });
    });
    resultatsFiltres.forEach(r => {
      const p = r.etudiant_pole || '—';
      const n = r.etudiant_niveau || '—';
      const cell = cells[p][n];
      cell.total++;
      if (r.valide) cell.valides++;
      else cell.ajournes++;
      const m = parseFloat(r.moyenne_semestre || '');
      if (!isNaN(m)) cell.moy += m;
    });
    poles.forEach(p => niveaux.forEach(n => {
      const c = cells[p][n];
      c.taux = c.total > 0 ? (c.valides / c.total) * 100 : 0;
      c.moy = c.total > 0 ? c.moy / c.total : 0;
    }));
    return { poles, niveaux, cells };
  }, [resultatsFiltres]);

  // Options filtrées par pôle (pour le selector)
  const optionsFiltrees = useMemo(() => {
    if (!pole) return options;
    return options.filter(o => o.pole_nom === pole);
  }, [options, pole]);

  const classesFiltrees = useMemo(() => {
    // Si niveau vide ('Tous'), on garde toutes les classes ; sinon on filtre.
    let list = niveau ? classes.filter(c => c.niveau === niveau) : [...classes];
    if (pole) list = list.filter(c => c.pole_nom === pole);
    if (option) list = list.filter(c => c.option_nom === option);
    return list;
  }, [classes, niveau, pole, option]);

  // ─── EXPORT EXCEL ──────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    if (resultatsFiltres.length === 0) {
      alert('Aucun résultat à exporter');
      return;
    }
    const data = resultatsFiltres.map(r => ({
      'Rang': r.rang_local,
      'Matricule': r.etudiant_matricule,
      'Nom': r.etudiant_nom,
      'Niveau': r.etudiant_niveau,
      'Classe': r.etudiant_classe,
      'Option': r.etudiant_option,
      'Moyenne': r.moyenne_semestre || '',
      'Crédits': `${r.credits_obtenus}/${r.credits_requis}`,
      'Mention': r.mention || '',
      'Décision': r.decision,
      'Validé': r.valide ? 'Oui' : 'Non',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    // Largeur colonnes
    ws['!cols'] = [
      { wch: 6 }, { wch: 14 }, { wch: 30 }, { wch: 8 }, { wch: 12 },
      { wch: 25 }, { wch: 9 }, { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 8 },
    ];

    // Feuille statistiques
    const statsData = [
      ['STATISTIQUES DELIBERATION'],
      [''],
      ['Niveau', niveau, 'Semestre', `S${semestre}`],
      ['Pôle', pole || 'Tous', 'Option', option || 'Toutes'],
      ['Classe', classe || 'Toutes', 'Année', anneeTravail || ''],
      [''],
      ['Total étudiants', stats.total],
      ['Validés', stats.valides, 'Taux réussite', `${stats.tauxReussite.toFixed(1)}%`],
      ['Ajournés', stats.ajournes, 'Taux échec', `${stats.tauxEchec.toFixed(1)}%`],
      ['Moyenne générale', stats.moyGenerale.toFixed(2)],
      [''],
      ['DISTRIBUTION PAR MENTION'],
      ['Mention', 'Effectif', 'Pourcentage'],
      ...dataMentions.map(m => [
        m.name, m.value, stats.total > 0 ? `${(m.value / stats.total * 100).toFixed(1)}%` : '0%',
      ]),
      [''],
      ['DISTRIBUTION PAR TRANCHE DE MOYENNE'],
      ['Tranche', 'Effectif'],
      ...dataTranches.map(t => [t.range, t.count]),
      [''],
      ['STATISTIQUES PAR CLASSE'],
      ['Classe', 'Total', 'Validés', 'Ajournés', 'Taux réussite (%)', 'Moyenne'],
      ...dataParClasse.map(c => [
        c.classe, c.total, c.valides, c.ajournes,
        c.tauxReussite.toFixed(1), c.moyenne.toFixed(2),
      ]),
    ];
    const wsStats = XLSX.utils.aoa_to_sheet(statsData);
    wsStats['!cols'] = [{ wch: 25 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Étudiants');
    XLSX.utils.book_append_sheet(wb, wsStats, 'Statistiques');

    const filtre_lbl = `${niveau}_S${semestre}${pole ? '_' + pole : ''}${option ? '_' + option : ''}`;
    const filename = `Deliberation_${filtre_lbl}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // ─── EXPORT PDF ────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    if (resultatsFiltres.length === 0) {
      alert('Aucun résultat à exporter');
      return;
    }
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();

    // En-tête
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('TABLEAU DE DÉLIBÉRATION', w / 2, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const subtitle = [
      `Année ${anneeTravail || '—'}`,
      `Niveau ${niveau}`,
      `Semestre S${semestre}`,
      pole ? `Pôle ${pole}` : '',
      option ? `Option ${option}` : '',
      classe ? `Classe ${classe}` : '',
    ].filter(Boolean).join(' · ');
    doc.text(subtitle, w / 2, 22, { align: 'center' });

    // Stats résumé
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${stats.total}  |  Validés: ${stats.valides} (${stats.tauxReussite.toFixed(1)}%)  |  Ajournés: ${stats.ajournes} (${stats.tauxEchec.toFixed(1)}%)  |  Moy. générale: ${stats.moyGenerale.toFixed(2)}/20`, w / 2, 30, { align: 'center' });

    // Table étudiants
    autoTable(doc, {
      startY: 35,
      head: [['Rang', 'Matricule', 'Nom', 'Classe', 'Option', 'Moyenne', 'Crédits', 'Mention', 'Décision']],
      body: resultatsFiltres.map(r => [
        String(r.rang_local || ''),
        r.etudiant_matricule || '',
        r.etudiant_nom || '',
        r.etudiant_classe || '',
        r.etudiant_option || '',
        r.moyenne_semestre || '',
        `${r.credits_obtenus}/${r.credits_requis}`,
        r.mention || '',
        r.decision || '',
      ]),
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 8) {
          const dec = (data.cell.raw as string || '').toLowerCase();
          if (dec.includes('admis') || dec.includes('valid')) {
            data.cell.styles.fillColor = [220, 252, 231];
            data.cell.styles.textColor = [22, 101, 52];
          } else if (dec.includes('ajourn') || dec.includes('refus')) {
            data.cell.styles.fillColor = [254, 226, 226];
            data.cell.styles.textColor = [153, 27, 27];
          } else if (dec.includes('rattrap')) {
            data.cell.styles.fillColor = [255, 237, 213];
            data.cell.styles.textColor = [154, 52, 18];
          }
        }
      },
    });

    // Page stats par mention
    doc.addPage('landscape');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('STATISTIQUES DÉTAILLÉES', w / 2, 15, { align: 'center' });

    autoTable(doc, {
      startY: 25,
      head: [['Mention', 'Effectif', 'Pourcentage']],
      body: dataMentions.map(m => [
        m.name, String(m.value),
        stats.total > 0 ? `${(m.value / stats.total * 100).toFixed(1)}%` : '0%',
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    });

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 10,
      head: [['Tranche moyenne', 'Effectif']],
      body: dataTranches.map(t => [t.range, String(t.count)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    });

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 10,
      head: [['Classe', 'Total', 'Validés', 'Ajournés', 'Taux réussite', 'Moyenne']],
      body: dataParClasse.map(c => [
        c.classe, String(c.total), String(c.valides), String(c.ajournes),
        `${c.tauxReussite.toFixed(1)}%`, c.moyenne.toFixed(2),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
    });

    // ─── PAGE SYNTHÈSE GLOBALE — Par pôle / Par niveau / Matrice ────────────
    doc.addPage('landscape');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('SYNTHÈSE GLOBALE', w / 2, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `${stats.valides}/${stats.total} validés (${stats.tauxReussite.toFixed(1)}%)`,
      w / 2, 21, { align: 'center' },
    );

    // Bloc Par pôle
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Par pôle', 14, 30);
    autoTable(doc, {
      startY: 33,
      head: [['Pôle', 'Total', 'Validés', 'Ajournés', 'Taux', 'Moyenne']],
      body: dataParPole.map(d => [
        d.label, String(d.total), String(d.valides), String(d.ajournes),
        `${d.tauxReussite.toFixed(1)}%`, d.moyenne.toFixed(2),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: 14, right: w / 2 + 2 },
    });

    // Bloc Par niveau (à droite)
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Par niveau', w / 2 + 5, 30);
    autoTable(doc, {
      startY: 33,
      head: [['Niveau', 'Total', 'Validés', 'Ajournés', 'Taux', 'Moyenne']],
      body: dataParNiveau.map(d => [
        d.label, String(d.total), String(d.valides), String(d.ajournes),
        `${d.tauxReussite.toFixed(1)}%`, d.moyenne.toFixed(2),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: w / 2 + 5, right: 14 },
    });

    // Matrice Pôle × Niveau
    const matriceStartY = Math.max(
      (doc as any).lastAutoTable.finalY + 12,
      80,
    );
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Matrice Pôle × Niveau — Validés / Ajournés / Taux', w / 2, matriceStartY, { align: 'center' });

    // Construire le tableau croisé
    const { poles: matPoles, niveaux: matNiveaux, cells: matCells } = dataPoleNiveau;
    const matHead = [['Pôle ↓ · Niveau →', ...matNiveaux, 'Total']];
    const matBody: any[] = [];
    matPoles.forEach(p => {
      const row: any[] = [p];
      let totalP = 0, validesP = 0, ajournesP = 0;
      matNiveaux.forEach(n => {
        const c = matCells[p][n];
        totalP += c.total; validesP += c.valides; ajournesP += c.ajournes;
        row.push(c.total > 0
          ? `${c.valides} / ${c.ajournes}\n(${c.taux.toFixed(0)}%)`
          : '—');
      });
      const tauxP = totalP > 0 ? (validesP / totalP) * 100 : 0;
      row.push(totalP > 0 ? `${validesP} / ${ajournesP}\n(${tauxP.toFixed(0)}%)` : '—');
      matBody.push(row);
    });
    // Ligne totale par niveau
    const totalRow: any[] = ['Total'];
    let grandTotal = 0, grandValides = 0, grandAjournes = 0;
    matNiveaux.forEach(n => {
      let totalN = 0, validesN = 0, ajournesN = 0;
      matPoles.forEach(p => {
        const c = matCells[p][n];
        totalN += c.total; validesN += c.valides; ajournesN += c.ajournes;
      });
      grandTotal += totalN; grandValides += validesN; grandAjournes += ajournesN;
      const tauxN = totalN > 0 ? (validesN / totalN) * 100 : 0;
      totalRow.push(totalN > 0 ? `${validesN} / ${ajournesN}\n(${tauxN.toFixed(0)}%)` : '—');
    });
    const grandTaux = grandTotal > 0 ? (grandValides / grandTotal) * 100 : 0;
    totalRow.push(`${grandValides} / ${grandAjournes}\n(${grandTaux.toFixed(0)}%)`);
    matBody.push(totalRow);

    autoTable(doc, {
      startY: matriceStartY + 4,
      head: matHead,
      body: matBody,
      styles: { fontSize: 9, halign: 'center', cellPadding: 2 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === matBody.length - 1) {
          // Ligne Total : mise en valeur
          data.cell.styles.fillColor = [219, 234, 254];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.section === 'body' && data.column.index === matHead[0].length - 1) {
          // Colonne Total : mise en valeur
          data.cell.styles.fillColor = [219, 234, 254];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    // Pied de page : page X/Y
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Page ${i} / ${pages}  ·  Généré le ${new Date().toLocaleString('fr-FR')}`,
        w / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }
      );
    }

    const filtre_lbl = `${niveau}_S${semestre}${pole ? '_' + pole : ''}${option ? '_' + option : ''}`;
    doc.save(`Deliberation_${filtre_lbl}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6" ref={printRef}>
      <div className="max-w-screen-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Award className="text-amber-500" size={28} />
              Tableau de Délibération
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Vue jury · {anneeTravail || '—'} · {niveau} · S{semestre}
              {pole && ` · ${pole}`}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* ── Action principale ────────────────────────────── */}
            <button
              onClick={handleCalculer}
              disabled={isCalculating}
              className="inline-flex items-center gap-1.5 h-9 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              title={`Recalculer les résultats du niveau ${niveau}`}
            >
              {isCalculating ? <Loader size={13} className="animate-spin" /> : <Calculator size={13} />}
              {isCalculating ? 'Calcul…' : `Calculer ${niveau || 'niveau'}`}
            </button>

            <button
              onClick={() => { setRecalcCSResult(null); setRecalcCSError(null); setShowRecalcCSModal(true); }}
              className="inline-flex items-center gap-1.5 h-9 px-3 bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-semibold rounded-md shadow-sm transition-colors"
              title="Recalculer les moyennes des étudiants Cours du Soir (formule arithmétique sans pondération UE). Sans impact sur FI/DD."
            >
              <Moon size={13} /> Recalcul CS
            </button>

            {/* Séparateur vertical */}
            <span className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-0.5" />

            {/* ── Exports (dropdown) ───────────────────────────── */}
            <div className="relative">
              <button
                onClick={() => setOpenMenu(openMenu === 'exports' ? null : 'exports')}
                disabled={exportingExcel}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md shadow-sm disabled:opacity-60 transition-colors"
                title="Exports Excel & PDF"
              >
                {exportingExcel ? <Loader size={13} className="animate-spin" /> : <Download size={13} />}
                Exports
                <ChevronDown size={12} className={`transition-transform ${openMenu === 'exports' ? 'rotate-180' : ''}`} />
              </button>
              {openMenu === 'exports' && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                    <button
                      onClick={() => { setOpenMenu(null); handleExportExcelDetail(); }}
                      className="w-full flex items-start gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <Download size={13} className="mt-0.5 text-emerald-600" />
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">Excel détaillé (par matière)</div>
                        <div className="text-gray-500 text-[10px]">Dev1, Dev2, Examen, Rattrapage, Moy. ECUE — 1 ligne / matière</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setOpenMenu(null); handleExportExcel(); }}
                      className="w-full flex items-start gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <FileSpreadsheet size={13} className="mt-0.5 text-green-600" />
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">Excel synthèse (par étudiant)</div>
                        <div className="text-gray-500 text-[10px]">Vue jury : rang, moyenne, mention, décision</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setOpenMenu(null); handleExportPDF(); }}
                      className="w-full flex items-start gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <FileText size={13} className="mt-0.5 text-red-600" />
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">PDF officiel</div>
                        <div className="text-gray-500 text-[10px]">Rapport de délibération à archiver</div>
                      </div>
                    </button>
                    <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                    <button
                      onClick={() => { setOpenMenu(null); setShowExportComparaison(true); }}
                      className="w-full flex items-start gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <FileSpreadsheet size={13} className="mt-0.5 text-blue-600" />
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">Comparaison avant/après</div>
                        <div className="text-gray-500 text-[10px]">Rachats batch + modifs manuelles + non touchés</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ── Rachats (dropdown) ────────────────────────────── */}
            <div className="relative">
              <button
                onClick={() => setOpenMenu(openMenu === 'rachats' ? null : 'rachats')}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-md shadow-sm transition-colors"
                title="Rachats"
              >
                <Calculator size={13} /> Rachats
                <ChevronDown size={12} className={`transition-transform ${openMenu === 'rachats' ? 'rotate-180' : ''}`} />
              </button>
              {openMenu === 'rachats' && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                    {/* Rachat réservé aux administrateurs (03/10/2026) */}
                    {estAdminRachat && (
                    <button
                      onClick={() => { setOpenMenu(null); setShowRachatBatch(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <Calculator size={13} className="text-purple-600" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Rachat intelligent en batch</span>
                    </button>
                    )}
                    <button
                      onClick={() => { setOpenMenu(null); setShowRapportRachats(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                    >
                      <FileText size={13} className="text-indigo-600" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">Rapport des rachats</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Fix 2026-09-06 v2 : les verrous ont été déplacés dans
                Portail Admin → Paramètres → Centre de Verrous.
                On garde juste un compteur + un lien direct. Le dropdown
                ancien est désactivé (via `false && (...)`) pour ne pas
                rompre les références aux states/handlers encore utilisés. */}
            <div className="relative">
              <a
                href="/portail-admin/parametres"
                className={`inline-flex items-center gap-1.5 h-9 px-3 text-white text-xs font-semibold rounded-md shadow-sm transition-colors ${
                  scansPortailActif === false || saisieNotesVerrouillee === true || verrouS1 === true || verrouS2 === true
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-slate-600 hover:bg-slate-700'
                }`}
                title="Ouvrir le Centre de Verrous dans Paramètres (nouvel emplacement)"
              >
                <Shield size={13} /> Verrous
                {(scansPortailActif === false || saisieNotesVerrouillee === true || verrouS1 === true || verrouS2 === true) && (
                  <span className="inline-flex items-center justify-center min-w-[16px] h-4 text-[9px] font-bold bg-white/25 rounded-full px-1">
                    {(scansPortailActif === false ? 1 : 0) + (saisieNotesVerrouillee === true ? 1 : 0) + (verrouS1 === true ? 1 : 0) + (verrouS2 === true ? 1 : 0)}
                  </span>
                )}
              </a>
              {false && (<>
              <button
                onClick={() => setOpenMenu(openMenu === 'admin' ? null : 'admin')}
                className="hidden"
              >
                <Shield size={13} /> Verrous
                <ChevronDown size={12} />
              </button>
              {openMenu === 'admin' && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                  <div className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1">
                    <button
                      onClick={() => { setOpenMenu(null); toggleScansPortail(); }}
                      disabled={scansPortailActif === null || togglingScans}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        {togglingScans ? <Loader size={13} className="animate-spin" />
                          : scansPortailActif === false ? <Lock size={13} className="text-orange-600" />
                          : <Unlock size={13} className="text-gray-500" />}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          Scans portail
                        </span>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        scansPortailActif === false
                          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      }`}>
                        {scansPortailActif === false ? 'Verrouillés' : 'Actifs'}
                      </span>
                    </button>
                    <button
                      onClick={() => { setOpenMenu(null); toggleVerrouSaisie(); }}
                      disabled={saisieNotesVerrouillee === null || togglingVerrouSaisie}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        {togglingVerrouSaisie ? <Loader size={13} className="animate-spin" />
                          : saisieNotesVerrouillee === true ? <Lock size={13} className="text-red-600" />
                          : <Unlock size={13} className="text-gray-500" />}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          Saisie des notes
                        </span>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        saisieNotesVerrouillee === true
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      }`}>
                        {saisieNotesVerrouillee === true ? 'Verrouillée' : 'Ouverte'}
                      </span>
                    </button>

                    {/* Verrous par semestre — indépendants du verrou global */}
                    {(['S1', 'S2'] as const).map(sem => {
                      const val = sem === 'S1' ? verrouS1 : verrouS2;
                      const inProgress = togglingSem === sem;
                      const label = sem === 'S1' ? 'Semestre 1 (S1/S3/S5)' : 'Semestre 2 (S2/S4/S6)';
                      return (
                        <button
                          key={sem}
                          onClick={() => { setOpenMenu(null); toggleVerrouSem(sem); }}
                          disabled={val === null || inProgress}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                        >
                          <div className="flex items-center gap-2">
                            {inProgress ? <Loader size={13} className="animate-spin" />
                              : val === true ? <Lock size={13} className="text-red-600" />
                              : <Unlock size={13} className="text-gray-500" />}
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {label}
                            </span>
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            val === true
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          }`}>
                            {val === true ? 'Verrouillée' : 'Ouverte'}
                          </span>
                        </button>
                      );
                    })}

                    {/* Fix 2026-08-25 : verrous granulaires parité × session */}
                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                      <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-400">
                        Saisie par session
                      </div>
                      {[
                        { key: 'S1_ORD', cle: 'saisie_notes_verrouillee_s1_ord', label: 'S1/S3/S5 · Ordinaire',    val: verrouS1Ord,  set: setVerrouS1Ord },
                        { key: 'S1_RATT', cle: 'saisie_notes_verrouillee_s1_ratt', label: 'S1/S3/S5 · Rattrapage', val: verrouS1Ratt, set: setVerrouS1Ratt },
                        { key: 'S2_ORD', cle: 'saisie_notes_verrouillee_s2_ord', label: 'S2/S4/S6 · Ordinaire',    val: verrouS2Ord,  set: setVerrouS2Ord },
                        { key: 'S2_RATT', cle: 'saisie_notes_verrouillee_s2_ratt', label: 'S2/S4/S6 · Rattrapage', val: verrouS2Ratt, set: setVerrouS2Ratt },
                      ].map(item => {
                        const inProgress = togglingGranular === item.key;
                        return (
                          <button
                            key={item.key}
                            onClick={() => { setOpenMenu(null); toggleParamBool(
                              item.cle, `Saisie ${item.label}`, item.val, item.set,
                              `Verrou saisie granulaire — ${item.label}. Seul le super_admin passe.`,
                              item.key,
                            ); }}
                            disabled={item.val === null || inProgress}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2">
                              {inProgress ? <Loader size={12} className="animate-spin" />
                                : item.val === true ? <Lock size={12} className="text-red-600" />
                                : <Unlock size={12} className="text-gray-400" />}
                              <span className="text-gray-800 dark:text-gray-200">{item.label}</span>
                            </div>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              item.val === true
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            }`}>
                              {item.val === true ? 'Verrou' : 'Ouv.'}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                      <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-400">
                        Scans copies portail
                      </div>
                      {[
                        { key: 'SCAN_S1_ORD', cle: 'portail_scans_verrouille_s1_ord', label: 'S1/S3/S5 · Ordinaire', val: scansS1Ord, set: setScansS1Ord },
                        { key: 'SCAN_S1_RATT', cle: 'portail_scans_verrouille_s1_ratt', label: 'S1/S3/S5 · Rattrapage', val: scansS1Ratt, set: setScansS1Ratt },
                        { key: 'SCAN_S2_ORD', cle: 'portail_scans_verrouille_s2_ord', label: 'S2/S4/S6 · Ordinaire', val: scansS2Ord, set: setScansS2Ord },
                        { key: 'SCAN_S2_RATT', cle: 'portail_scans_verrouille_s2_ratt', label: 'S2/S4/S6 · Rattrapage', val: scansS2Ratt, set: setScansS2Ratt },
                      ].map(item => {
                        const inProgress = togglingGranular === item.key;
                        return (
                          <button
                            key={item.key}
                            onClick={() => { setOpenMenu(null); toggleParamBool(
                              item.cle, `Scans ${item.label}`, item.val, item.set,
                              `Verrou scans copies portail (étudiant + parent) — ${item.label}.`,
                              item.key,
                            ); }}
                            disabled={item.val === null || inProgress}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2">
                              {inProgress ? <Loader size={12} className="animate-spin" />
                                : item.val === true ? <Lock size={12} className="text-red-600" />
                                : <Unlock size={12} className="text-gray-400" />}
                              <span className="text-gray-800 dark:text-gray-200">{item.label}</span>
                            </div>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              item.val === true
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            }`}>
                              {item.val === true ? 'Verrou' : 'Ouv.'}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Fix 2026-08-28 : masquage granulaire des notes d'examens.
                        Devoirs restent visibles côté portail — utile pendant
                        la saisie/vérification des examens. */}
                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                      <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase font-bold tracking-wider text-amber-600 dark:text-amber-400">
                        🎯 Masquer uniquement les notes d'examens (devoirs restent visibles)
                      </div>
                      {[
                        { key: 'MASK_ALL', cle: 'portail_masquer_examens', label: 'Tous semestres (S1+S2+S3+…)', val: masquerExamensGlobal, set: setMasquerExamensGlobal },
                        { key: 'MASK_S1', cle: 'portail_masquer_examens_s1', label: 'S1/S3/S5 uniquement', val: masquerExamensS1, set: setMasquerExamensS1 },
                        { key: 'MASK_S2', cle: 'portail_masquer_examens_s2', label: 'S2/S4/S6 uniquement', val: masquerExamensS2, set: setMasquerExamensS2 },
                      ].map(item => {
                        const inProgress = togglingGranular === item.key;
                        return (
                          <button
                            key={item.key}
                            onClick={() => { setOpenMenu(null); toggleParamBool(
                              item.cle,
                              `Masquage examens portail — ${item.label}`,
                              item.val, item.set,
                              `Masque uniquement les notes d'examens (et moyennes calculées). Les notes de devoirs restent visibles côté étudiant/parent. Utile pendant la saisie ou la vérification des examens. — ${item.label}.`,
                              item.key,
                            ); }}
                            disabled={item.val === null || inProgress}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-amber-50 dark:hover:bg-amber-900/20 text-left disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2">
                              {inProgress ? <Loader size={12} className="animate-spin" />
                                : item.val === true ? <Lock size={12} className="text-amber-600" />
                                : <Unlock size={12} className="text-gray-400" />}
                              <span className="text-gray-800 dark:text-gray-200">{item.label}</span>
                            </div>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              item.val === true
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            }`}>
                              {item.val === true ? 'Masqué' : 'Visible'}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                      <div className="px-3 pt-1 pb-0.5 text-[9px] uppercase font-bold tracking-wider text-gray-500 dark:text-gray-400">
                        Consultation relevés portail
                      </div>
                      {[
                        { key: 'PORT_S1', cle: 'portail_releves_verrouille_s1', label: 'S1/S3/S5 (portail étudiant + parent)', val: portailReleveS1, set: setPortailReleveS1 },
                        { key: 'PORT_S2', cle: 'portail_releves_verrouille_s2', label: 'S2/S4/S6 (portail étudiant + parent)', val: portailReleveS2, set: setPortailReleveS2 },
                      ].map(item => {
                        const inProgress = togglingGranular === item.key;
                        return (
                          <button
                            key={item.key}
                            onClick={() => { setOpenMenu(null); toggleParamBool(
                              item.cle, `Consultation ${item.label}`, item.val, item.set,
                              `Verrou consultation relevés portail (étudiant + parent) — ${item.label}. Réponse HTTP 423.`,
                              item.key,
                            ); }}
                            disabled={item.val === null || inProgress}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 text-left disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2">
                              {inProgress ? <Loader size={12} className="animate-spin" />
                                : item.val === true ? <Lock size={12} className="text-red-600" />
                                : <Unlock size={12} className="text-gray-400" />}
                              <span className="text-gray-800 dark:text-gray-200">{item.label}</span>
                            </div>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              item.val === true
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            }`}>
                              {item.val === true ? 'Verrou' : 'Ouv.'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              </>)}
            </div>
          </div>
        </div>

        {exportError && (
          <div className="rounded-lg p-3 text-sm flex items-center justify-between gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200">
            <span className="flex items-center gap-2"><AlertCircle size={14} /> Export Excel : {exportError}</span>
            <button onClick={() => setExportError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded"><X size={14} /></button>
          </div>
        )}

        {calcMessage && (
          <div className={`rounded-lg p-3 text-sm flex items-center justify-between gap-3 ${
            calcMessage.startsWith('❌')
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200'
              : (calcResult?.errors?.length ?? 0) > 0
                ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-orange-800 dark:text-orange-200'
                : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200'
          }`}>
            <span>{calcMessage}</span>
            <div className="flex items-center gap-2">
              {(calcResult?.errors?.length ?? 0) > 0 && (
                <button
                  onClick={() => setShowErrorsModal(true)}
                  className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-current rounded font-medium hover:bg-gray-50"
                >
                  Voir les erreurs ({calcResult.errors.length})
                </button>
              )}
              <button onClick={() => setCalcMessage(null)} className="text-current"><X size={14} /></button>
            </div>
          </div>
        )}

        {showErrorsModal && calcResult?.erreurs_detail && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowErrorsModal(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">⚠ Erreurs de calcul ({calcResult.erreurs_detail.length})</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Le calcul a abouti malgré ces erreurs. Corrigez les données puis relancez le calcul.
                  </div>
                </div>
                <button onClick={() => setShowErrorsModal(false)} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Matricule</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Élément</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Erreur</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {calcResult.erreurs_detail.map((e: any, i: number) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                        <td className="px-3 py-2 font-mono text-xs">{e.matricule || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                            e.type === 'ECUE' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : e.type === 'UE' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                              : e.type === 'Semestre' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>{e.type}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">{e.detail}</td>
                        <td className="px-3 py-2 text-xs text-red-700 dark:text-red-400">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-2 border-t border-gray-200 dark:border-gray-700 text-right">
                <button
                  onClick={() => setShowErrorsModal(false)}
                  className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded"
                >Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Niveau</label>
              <select value={niveau} onChange={e => { setNiveau(e.target.value); setClasse(''); }} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Tous les niveaux</option>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Semestre</label>
              <select value={semestre} onChange={e => setSemestre(parseInt(e.target.value))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value={1}>S1</option>
                <option value={2}>S2</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Pôle</label>
              <select value={pole} onChange={e => { setPole(e.target.value); setOption(''); setClasse(''); }} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[140px]">
                <option value="">Tous les pôles</option>
                {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Option</label>
              <select value={option} onChange={e => { setOption(e.target.value); setClasse(''); }} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[180px]">
                <option value="">Toutes les options</option>
                {optionsFiltrees.map(o => <option key={o.id} value={o.nom}>{o.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Classe</label>
              <select value={classe} onChange={e => setClasse(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Toutes</option>
                {classesFiltrees.map(c => <option key={c.id} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Type formation</label>
              <select value={typeFormation} onChange={e => setTypeFormation(e.target.value as any)} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="FI_DD">FI + DD (par défaut)</option>
                <option value="FI">FI uniquement</option>
                <option value="DD">DD uniquement</option>
                <option value="CS">CS (Cours du Soir)</option>
                <option value="TOUS">Tous</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Session</label>
              <select value={sessionFilter}
                onChange={e => setSessionFilter(e.target.value as any)}
                className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Toutes</option>
                <option value="normale">Session ordinaire</option>
                <option value="rattrapage">Session rattrapage</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Décision</label>
              <select value={decisionFilter} onChange={e => setDecisionFilter(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Toutes</option>
                <option value="admis">Admis</option>
                <option value="rattrapage">Rattrapage</option>
                <option value="ajourné">Ajourné</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Tranche moyenne
                {(moyenneMin || moyenneMax) && (
                  <button onClick={() => { setMoyenneMin(''); setMoyenneMax(''); }}
                    className="ml-1 text-blue-500 hover:text-blue-700 normal-case font-normal text-[10px]">
                    (effacer)
                  </button>
                )}
              </label>
              <div className="flex items-center gap-1">
                <input type="number" step="0.01" min="0" max="20"
                  value={moyenneMin} onChange={e => setMoyenneMin(e.target.value)}
                  placeholder="min"
                  className="w-16 px-1.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                <span className="text-xs text-gray-400">→</span>
                <input type="number" step="0.01" min="0" max="20"
                  value={moyenneMax} onChange={e => setMoyenneMax(e.target.value)}
                  placeholder="max"
                  className="w-16 px-1.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Recherche</label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Matricule ou nom…"
                  className="w-full pl-7 pr-7 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Délibération : le périmètre suit le filtre « Type formation » */}
        {(typeFormation === 'CS' || typeFormation === 'TOUS') && (
          <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2">
            {typeFormation === 'CS'
              ? "Les statistiques de délibération ci-dessous portent sur les cours du soir uniquement."
              : "Les statistiques de délibération ci-dessous portent sur toutes les formations (FI, DD et cours du soir)."}
          </div>
        )}
        <StatsDeliberation
          semestre={semestre as 1 | 2}
          annee={anneeTravail}
          pole={pole}
          option={option}
          classe={classe}
          niveau={niveau}
          typeFormation={typeFormation}
        />

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Ces cartes portent sur la liste affichée (résultats calculés, filtres appliqués) ;
              les statistiques ci-dessus et le PDF raisonnent sur l'effectif inscrit. */}
          <KPI label="Résultats affichés" value={stats.total} icon={<Users size={18} />} color="blue" />
          <KPI label="Validés" value={stats.valides} sub={`${pctListe(stats.valides, stats.total)} des résultats`} icon={<CheckCircle2 size={18} />} color="green" />
          <KPI label="Ajournés" value={stats.ajournes} sub={`${pctListe(stats.ajournes, stats.total)} des résultats`} icon={<AlertCircle size={18} />} color="red" />
          <KPI label="Moy. générale" value={stats.moyGenerale.toFixed(2)} icon={<Target size={18} />} color="purple" />
          <KPI label="Bien & +" value={stats.sup14} sub={`${pctListe(stats.sup14, stats.total)} des résultats`} icon={<Award size={18} />} color="amber" />
          <KPI label="Excellents" value={stats.sup16} sub={`${pctListe(stats.sup16, stats.total)} des résultats`} icon={<TrendingUp size={18} />} color="emerald" />
        </div>

        {/* Détail Validés : ordinaire vs rattrapage — aide à comprendre les écarts vs PDF stats */}
        {stats.total > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 inline-flex items-center gap-2">
                <CheckCircle2 size={15} className="text-emerald-600" />
                Détail des validés ({stats.valides})
              </h3>
              <div className="text-xs text-gray-500">
                Le chiffre <strong>Validés</strong> ci-dessus = ordinaire + rattrapage (+ autres) ; valeur calculée
                en temps réel sur la liste filtrée.
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/15 rounded-lg p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-green-700 dark:text-green-300">
                  Session ordinaire
                </div>
                <div className="text-2xl font-bold tabular-nums text-green-700 dark:text-green-300">
                  {stats.validesOrdinaire}
                </div>
                <div className="text-[10px] text-green-700/70 dark:text-green-300/70">
                  {stats.valides > 0 ? ((stats.validesOrdinaire / stats.valides) * 100).toFixed(1) : 0}% des validés
                </div>
              </div>
              <div className="border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/15 rounded-lg p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300">
                  Après rattrapage
                </div>
                <div className="text-2xl font-bold tabular-nums text-amber-700 dark:text-amber-300">
                  {stats.validesRattrapage}
                </div>
                <div className="text-[10px] text-amber-700/70 dark:text-amber-300/70">
                  {stats.valides > 0 ? ((stats.validesRattrapage / stats.valides) * 100).toFixed(1) : 0}% des validés
                </div>
              </div>
              {stats.validesAutre > 0 && (
                <div className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/15 rounded-lg p-2.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-700 dark:text-gray-300">
                    Autres
                  </div>
                  <div className="text-2xl font-bold tabular-nums text-gray-700 dark:text-gray-300">
                    {stats.validesAutre}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    session_validation non renseigné
                  </div>
                </div>
              )}
              <div className="border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/15 rounded-lg p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-red-700 dark:text-red-300">
                  Ajournés
                </div>
                <div className="text-2xl font-bold tabular-nums text-red-700 dark:text-red-300">
                  {stats.ajournes}
                </div>
                <div className="text-[10px] text-red-700/70 dark:text-red-300/70">
                  {stats.tauxEchec.toFixed(1)}% du total
                </div>
              </div>
            </div>
            {Object.keys(stats.decisionsBreakdown).length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
                  Par décision exacte
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(stats.decisionsBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([decision, n]) => (
                      <span key={decision}
                        className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 rounded">
                        {decision} : <strong>{n}</strong>
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charts */}
        {stats.total > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Donut mentions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <BarChart3 size={16} /> Distribution par mention
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={dataMentions}
                    cx="50%" cy="50%"
                    labelLine={false}
                    label={(p: any) => `${p.name}: ${p.value}`}
                    outerRadius={80}
                    innerRadius={45}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {dataMentions.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Histogramme tranches */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <BarChart3 size={16} /> Distribution des moyennes
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dataTranches}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6">
                    <LabelList dataKey="count" position="top" style={{ fontSize: 11 }} />
                    {dataTranches.map((t, idx) => (
                      <Cell key={`c-${idx}`} fill={t.min >= 10 ? '#10b981' : t.min >= 8 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Bar par classe */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                <TrendingDown size={16} /> Taux réussite par classe (top 10)
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dataParClasse.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="classe" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip formatter={(v: any) => `${v.toFixed(1)}%`} />
                  <Bar dataKey="tauxReussite" fill="#10b981">
                    <LabelList dataKey="tauxReussite" position="right" formatter={(v: any) => `${v.toFixed(0)}%`} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Stats par pôle + par niveau + matrice pôle × niveau */}
        {resultatsFiltres.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AggregBloc titre="Par pôle" data={dataParPole} />
            <AggregBloc titre="Par niveau" data={dataParNiveau} />
          </div>
        )}

        {dataPoleNiveau.poles.length > 1 && dataPoleNiveau.niveaux.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200">
                Matrice Pôle × Niveau — Validés / Ajournés / Taux
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/30 text-[10px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left sticky left-0 bg-gray-50 dark:bg-gray-700/30">Pôle ↓ · Niveau →</th>
                    {dataPoleNiveau.niveaux.map(n => (
                      <th key={n} className="px-3 py-2 text-center font-semibold">{n}</th>
                    ))}
                    <th className="px-3 py-2 text-center font-semibold bg-blue-50/50 dark:bg-blue-900/20">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {dataPoleNiveau.poles.map(p => {
                    const totalPole = dataPoleNiveau.niveaux.reduce((s, n) => s + dataPoleNiveau.cells[p][n].total, 0);
                    const validesPole = dataPoleNiveau.niveaux.reduce((s, n) => s + dataPoleNiveau.cells[p][n].valides, 0);
                    return (
                      <tr key={p} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 font-bold text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">{p}</td>
                        {dataPoleNiveau.niveaux.map(n => {
                          const c = dataPoleNiveau.cells[p][n];
                          if (c.total === 0) {
                            return <td key={n} className="px-3 py-2 text-center text-gray-300">—</td>;
                          }
                          return (
                            <td key={n} className="px-3 py-2 text-center">
                              <div className="font-bold text-[11px]">
                                <span className="text-green-600 dark:text-green-400">{c.valides}</span>
                                <span className="text-gray-400"> / </span>
                                <span className="text-red-600 dark:text-red-400">{c.ajournes}</span>
                              </div>
                              <div className={`text-[10px] font-semibold ${
                                c.taux >= 70 ? 'text-green-600' :
                                c.taux >= 50 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {c.taux.toFixed(0)}%
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-center bg-blue-50/30 dark:bg-blue-900/10">
                          <div className="font-bold text-[11px]">
                            <span className="text-green-600 dark:text-green-400">{validesPole}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-red-600 dark:text-red-400">{totalPole - validesPole}</span>
                          </div>
                          <div className={`text-[10px] font-semibold ${
                            totalPole > 0 && validesPole / totalPole >= 0.7 ? 'text-green-600' :
                            totalPole > 0 && validesPole / totalPole >= 0.5 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {totalPole > 0 ? ((validesPole / totalPole) * 100).toFixed(0) : 0}%
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Ligne Total par niveau */}
                  <tr className="bg-gray-50 dark:bg-gray-700/50 font-bold">
                    <td className="px-3 py-2 sticky left-0 bg-gray-50 dark:bg-gray-700/50">Total</td>
                    {dataPoleNiveau.niveaux.map(n => {
                      const totalN = dataPoleNiveau.poles.reduce((s, p) => s + dataPoleNiveau.cells[p][n].total, 0);
                      const validesN = dataPoleNiveau.poles.reduce((s, p) => s + dataPoleNiveau.cells[p][n].valides, 0);
                      return (
                        <td key={n} className="px-3 py-2 text-center">
                          <div className="text-[11px]">
                            <span className="text-green-600 dark:text-green-400">{validesN}</span>
                            <span className="text-gray-400"> / </span>
                            <span className="text-red-600 dark:text-red-400">{totalN - validesN}</span>
                          </div>
                          <div className={`text-[10px] ${
                            totalN > 0 && validesN / totalN >= 0.7 ? 'text-green-600' :
                            totalN > 0 && validesN / totalN >= 0.5 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {totalN > 0 ? ((validesN / totalN) * 100).toFixed(0) : 0}%
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center bg-blue-100 dark:bg-blue-900/30">
                      {stats.total}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stats par classe — repliable + sélecteur de classe pour focus */}
        {dataParClasse.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button onClick={() => setShowStatsClasse(v => !v)}
              className="w-full px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-colors">
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                <span className={`inline-block transition-transform ${showStatsClasse ? 'rotate-90' : ''}`}>▶</span>
                Statistiques par classe
                <span className="text-xs font-normal text-gray-500">({dataParClasse.length} classes)</span>
              </h3>
              <span className="text-[11px] text-gray-500">{showStatsClasse ? 'Masquer' : 'Afficher'}</span>
            </button>

            {showStatsClasse && (
              <>
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-wrap">
                  <label className="text-[11px] text-gray-600 dark:text-gray-400">Focus :</label>
                  <select value={classeStatsFocus}
                    onChange={e => setClasseStatsFocus(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <option value="">Toutes les classes</option>
                    {dataParClasse.map(c => (
                      <option key={c.classe} value={c.classe}>
                        {c.classe} — {c.total} étud. · {c.tauxReussite.toFixed(0)} %
                      </option>
                    ))}
                  </select>
                  {classeStatsFocus && (
                    <button onClick={() => setClasseStatsFocus('')}
                      className="text-[11px] text-blue-500 hover:text-blue-700">
                      Effacer
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/30 text-xs text-gray-500 uppercase sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Classe</th>
                        <th className="px-3 py-2 text-center font-semibold">Total</th>
                        <th className="px-3 py-2 text-center font-semibold">Validés</th>
                        <th className="px-3 py-2 text-center font-semibold">Ajournés</th>
                        <th className="px-3 py-2 text-center font-semibold">Taux réussite</th>
                        <th className="px-3 py-2 text-center font-semibold">Moyenne</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {dataParClasse
                        .filter(c => !classeStatsFocus || c.classe === classeStatsFocus)
                        .map(c => (
                          <tr key={c.classe} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-3 py-2 font-bold">{c.classe}</td>
                            <td className="px-3 py-2 text-center">{c.total}</td>
                            <td className="px-3 py-2 text-center text-green-700 dark:text-green-400 font-semibold">{c.valides}</td>
                            <td className="px-3 py-2 text-center text-red-700 dark:text-red-400 font-semibold">{c.ajournes}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`font-bold ${c.tauxReussite >= 70 ? 'text-green-600' : c.tauxReussite >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                {c.tauxReussite.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center font-mono">
                              <span className={c.moyenne >= 10 ? 'text-green-600' : 'text-red-600'}>{c.moyenne.toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Table principale étudiants */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Filtres rapides au-dessus de la table (pôle / niveau) — duplique les filtres
              du haut pour être à portée de main quand on travaille sur la liste. */}
          <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-blue-50/40 dark:bg-blue-900/10 flex items-center gap-3 flex-wrap text-xs">
            <Filter size={13} className="text-blue-600 dark:text-blue-400" />
            <span className="font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 text-[10px]">
              Filtrer la liste :
            </span>
            <div className="inline-flex items-center gap-1.5">
              <label className="text-gray-600 dark:text-gray-400">Pôle</label>
              <select value={pole}
                onChange={e => { setPole(e.target.value); setOption(''); setClasse(''); }}
                className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Tous</option>
                {poles.map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
              </select>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <label className="text-gray-600 dark:text-gray-400">Niveau</label>
              <select value={niveau}
                onChange={e => { setNiveau(e.target.value); setClasse(''); }}
                className="px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="">Tous</option>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {(niveau || pole || option || classe || decisionFilter || sessionFilter || moyenneMin || moyenneMax || search) && (
              <button
                onClick={() => {
                  setNiveau(''); setPole(''); setOption(''); setClasse('');
                  setDecisionFilter(''); setSessionFilter('');
                  setMoyenneMin(''); setMoyenneMax(''); setSearch('');
                }}
                className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded font-medium"
                title="Réinitialiser tous les filtres"
              >
                <RefreshCw size={11} /> Réinitialiser
              </button>
            )}
          </div>

          <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2 flex-wrap">
              Étudiants ({resultatsFiltres.length})
              {niveau && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold normal-case">
                  Niveau : {niveau}
                  <button onClick={() => setNiveau('')} className="hover:opacity-70">
                    <X size={10} />
                  </button>
                </span>
              )}
              {pole && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-[10px] font-semibold normal-case">
                  Pôle : {pole}
                  <button onClick={() => { setPole(''); setOption(''); setClasse(''); }} className="hover:opacity-70">
                    <X size={10} />
                  </button>
                </span>
              )}
              {option && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-[10px] font-semibold normal-case">
                  Option : {option}
                  <button onClick={() => { setOption(''); setClasse(''); }} className="hover:opacity-70">
                    <X size={10} />
                  </button>
                </span>
              )}
              {classe && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold normal-case">
                  Classe : {classe}
                  <button onClick={() => setClasse('')} className="hover:opacity-70">
                    <X size={10} />
                  </button>
                </span>
              )}
              {(moyenneMin || moyenneMax) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold normal-case">
                  Tranche : {moyenneMin || '0'} → {moyenneMax || '20'}
                  <button onClick={() => { setMoyenneMin(''); setMoyenneMax(''); }}
                    className="hover:text-blue-900 dark:hover:text-white">
                    <X size={10} />
                  </button>
                </span>
              )}
              {sessionFilter && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold normal-case ${
                  sessionFilter === 'normale'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    : 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                }`}>
                  {sessionFilter === 'normale' ? 'Session ordinaire' : 'Session rattrapage'}
                  <button onClick={() => setSessionFilter('')}
                    className="hover:opacity-70">
                    <X size={10} />
                  </button>
                </span>
              )}
            </h3>
            {isLoading && <Loader size={14} className="animate-spin text-blue-500" />}
          </div>

          {/* Bannière étudiants sans résultat (fix affichage semestres pairs) */}
          {manquants && manquants.sans_resultat > 0 && (
            <div className="mx-3 mt-3 mb-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg text-sm">
              <button
                onClick={() => setManquantsOpen(o => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
              >
                <span className="text-amber-600 dark:text-amber-400">⚠</span>
                <span className="flex-1">
                  <b>{manquants.sans_resultat}</b> étudiant{manquants.sans_resultat > 1 ? 's' : ''} du périmètre
                  {' '}n'{manquants.sans_resultat > 1 ? 'ont' : 'a'} pas de résultat calculé pour ce semestre
                  {' '}(UE incomplètes){manquantsOpen ? '' : ' — cliquez pour voir la liste'}.
                </span>
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {manquants.avec_resultat}/{manquants.total_periometre} calculés
                </span>
              </button>
              {manquantsOpen && (
                <div className="border-t border-amber-200 dark:border-amber-800 max-h-72 overflow-y-auto">
                  {manquants.resultats.map(m => (
                    <div key={m.id} className="px-3 py-2 border-b border-amber-100 dark:border-amber-900/50 last:border-b-0 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {m.nom} {m.prenoms}
                            <span className="ml-2 font-mono text-[10px] text-gray-500">{m.matricule}</span>
                            <span className="ml-2 text-[10px] text-gray-500">· {m.classe}</span>
                          </div>
                        </div>
                        <div className="text-[10px] text-amber-700 dark:text-amber-300 shrink-0">
                          {m.nb_ues_calculees}/{m.nb_ues_attendues} UE
                        </div>
                      </div>
                      {m.ues_manquantes.length > 0 && (
                        <div className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 line-clamp-2">
                          Manquant : {m.ues_manquantes.map(u => u.code).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            {isLoading ? (
              <div className="p-12 text-center text-gray-500">
                <Loader size={32} className="animate-spin mx-auto mb-3" />
                Chargement des résultats…
              </div>
            ) : resultatsFiltres.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-semibold mb-2">Aucun résultat pour ces filtres</p>
                <p className="text-xs mt-1 mb-4">
                  Si c'est la première fois, lancez le calcul pour ce niveau (les moyennes et mentions
                  sont calculées à partir des notes saisies).
                </p>
                <button
                  onClick={handleCalculer}
                  disabled={isCalculating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded shadow disabled:opacity-50"
                >
                  {isCalculating ? <Loader size={14} className="animate-spin" /> : <Calculator size={14} />}
                  {isCalculating ? 'Calcul en cours…' : `Calculer pour le niveau ${niveau}`}
                </button>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500 uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-center font-semibold w-12">Rang</th>
                    <th className="px-2 py-2 text-left font-semibold w-24">Matricule</th>
                    <th className="px-2 py-2 text-left font-semibold">Nom</th>
                    <th className="px-2 py-2 text-left font-semibold w-20">Classe</th>
                    <th className="px-2 py-2 text-left font-semibold">Option</th>
                    <th className="px-2 py-2 text-center font-semibold w-20">Moyenne</th>
                    <th className="px-2 py-2 text-center font-semibold w-16">Crédits</th>
                    <th className="px-2 py-2 text-center font-semibold w-24">Mention</th>
                    <th className="px-2 py-2 text-center font-semibold w-24">Session</th>
                    <th className="px-2 py-2 text-center font-semibold w-32">Décision</th>
                    <th className="px-2 py-2 text-center font-semibold w-16">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {resultatsFiltres.map(r => (
                    <tr key={r.id} className="hover:bg-blue-50 dark:hover:bg-blue-900/10">
                      <td className="px-2 py-1.5 text-center font-bold text-gray-500">{r.rang_local}</td>
                      <td className="px-2 py-1.5 font-mono">{r.etudiant_matricule}</td>
                      <td className="px-2 py-1.5 font-medium">{r.etudiant_nom}</td>
                      <td className="px-2 py-1.5 font-bold text-blue-700 dark:text-blue-400">{r.etudiant_classe}</td>
                      <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300 truncate max-w-[180px]" title={r.etudiant_option}>{r.etudiant_option}</td>
                      <td className="px-2 py-1.5 text-center font-bold font-mono">
                        <span className={parseFloat(r.moyenne_semestre || '0') >= 10 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                          {r.moyenne_semestre || '—'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center text-gray-600">{r.credits_obtenus}/{r.credits_requis}</td>
                      <td className="px-2 py-1.5 text-center">
                        {r.mention && (
                          <span
                            className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold text-white"
                            style={{ backgroundColor: couleurMention(r.mention) }}
                          >
                            {r.mention}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {r.session_validation ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            r.session_validation === 'normale'
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                              : 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                          }`}>
                            {r.session_validation === 'normale' ? 'Ordinaire' : 'Rattrapage'}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-[10px]">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${decisionColor(r.decision)}`}>
                          {r.decision}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => setActionRapide({
                            etudiantId: r.etudiant,
                            matricule: r.etudiant_matricule,
                            nom: r.etudiant_nom,
                            moyenneActuelle: r.moyenne_semestre ? parseFloat(r.moyenne_semestre) : null,
                          })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
                          title="Voir/modifier les notes + recalculer la moyenne">
                          <Edit3 size={10} /> Notes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal action rapide (notes éditables + recalcul d'un étudiant) */}
      {actionRapide && (
        <ActionRapideDeliberationModal
          open={actionRapide !== null}
          etudiantId={actionRapide.etudiantId}
          matricule={actionRapide.matricule}
          nom={actionRapide.nom}
          semestre={semestre as 1 | 2}
          anneeAcademique={anneeTravail || ''}
          moyenneActuelle={actionRapide.moyenneActuelle}
          onClose={() => setActionRapide(null)}
        />
      )}

      {/* Modal rapport des rachats appliqués */}
      <RapportRachatsModal
        open={showRapportRachats}
        onClose={() => setShowRapportRachats(false)}
      />

      {/* Modal export comparaison avant/après rachat (vérification) */}
      <ExportComparaisonRachatsModal
        open={showExportComparaison}
        onClose={() => setShowExportComparaison(false)}
      />

      {/* Modal rachat intelligent en batch */}
      <RachatIntelligentBatchModal
        open={showRachatBatch}
        anneeAcademique={anneeActive?.libelle || ''}
        semestreDefaut={semestre as 1 | 2}
        scopeDefaut={{
          pole: pole || undefined,
          niveau: niveau || undefined,
          option: option || undefined,
          classe: classe || undefined,
        }}
        onClose={() => setShowRachatBatch(false)}
      />

      {/* Modal : recalcul des moyennes Cours du Soir (isolé de FI/DD) */}
      {showRecalcCSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !isRecalculatingCS && setShowRecalcCSModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Moon size={16} className="text-indigo-600" />
                Recalcul des moyennes Cours du Soir
              </h3>
              <button onClick={() => setShowRecalcCSModal(false)} disabled={isRecalculatingCS} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-gray-800 dark:text-gray-200 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded p-3">
                <p className="font-semibold mb-1">Mode de calcul CS (moyennes 100 % arithmétiques)</p>
                <ul className="text-xs space-y-0.5 pl-4 list-disc">
                  <li><strong>moyenne_UE</strong> = Σ moy_ECUE / nb_matières (moyenne simple des matières)</li>
                  <li><strong>moyenne_semestre</strong> = Σ moy_UE / nb_UE (moyenne simple des UE)</li>
                  <li>Si le semestre ne compte qu'une seule UE, la moyenne semestre = moyenne des matières.</li>
                </ul>
                <p className="text-xs mt-2 opacity-80">
                  Aucune pondération par crédits. Ce mode <strong>ne s'applique qu'aux étudiants type_formation=CS</strong>.
                  Les étudiants FI et DD ne sont jamais touchés (filtre en dur côté serveur).
                </p>
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-300">
                <div className="font-semibold mb-1">Périmètre du recalcul :</div>
                <ul className="space-y-0.5 list-disc list-inside">
                  <li>Semestre : <strong>{semestre === 1 || semestre === 2 ? `S${semestre}` : 'S1 et S2'}</strong></li>
                  <li>Niveau : <strong>{niveau || 'tous'}</strong></li>
                  <li>Classe : <strong>{classe || 'toutes classes CS'}</strong></li>
                  <li>Option : <strong>{option || 'toutes options'}</strong></li>
                </ul>
              </div>
              {recalcCSResult && (
                <div className="text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded p-3 text-emerald-800 dark:text-emerald-200">
                  <div className="font-semibold mb-1 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> Terminé
                  </div>
                  <ul className="space-y-0.5">
                    <li>Étudiants CS traités : <strong>{recalcCSResult.total_etudiants_cs}</strong></li>
                    <li>Semestres calculés : <strong>{recalcCSResult.semestres_calcules}</strong></li>
                    <li>Classes touchées : <strong>{recalcCSResult.classes_touchees.join(', ') || '—'}</strong></li>
                    {recalcCSResult.erreurs.length > 0 && (
                      <li className="text-red-600 dark:text-red-400">Erreurs : {recalcCSResult.erreurs.length}</li>
                    )}
                  </ul>
                </div>
              )}
              {recalcCSError && (
                <div className="text-xs bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-red-800 dark:text-red-200 flex items-start gap-2">
                  <AlertCircle size={14} /> {recalcCSError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowRecalcCSModal(false)}
                  disabled={isRecalculatingCS}
                  className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-40"
                >
                  {recalcCSResult ? 'Fermer' : 'Annuler'}
                </button>
                {!recalcCSResult && (
                  <button
                    onClick={handleRecalculerCS}
                    disabled={isRecalculatingCS}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-semibold rounded disabled:opacity-60"
                  >
                    {isRecalculatingCS ? <Loader size={12} className="animate-spin" /> : <Calculator size={12} />}
                    {isRecalculatingCS ? 'Recalcul en cours…' : 'Lancer le recalcul CS'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── KPI Card ──────────────────────────────────────────────────────────────
// Part d'une liste à une décimale ; une part non nulle arrondie à 0,0 % s'affiche « < 0.1% ».
function pctListe(n: number, total: number): string {
  if (total <= 0) return '0.0%';
  const p = (n * 100) / total;
  return p > 0 && p < 0.05 ? '< 0.1%' : `${p.toFixed(1)}%`;
}

// ─── Bloc d'agrégation par dimension (pôle, niveau) ────────────────────────
const AggregBloc: React.FC<{
  titre: string;
  data: Array<{ label: string; total: number; valides: number; ajournes: number; tauxReussite: number; moyenne: number }>;
}> = ({ titre, data }) => {
  if (data.length === 0) return null;
  const totalGlobal = data.reduce((s, d) => s + d.total, 0);
  const validesGlobal = data.reduce((s, d) => s + d.valides, 0);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200">{titre}</h3>
        <span className="text-[11px] text-gray-500">
          {validesGlobal}/{totalGlobal} validés ({totalGlobal > 0 ? ((validesGlobal / totalGlobal) * 100).toFixed(1) : 0}%)
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700/30 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Libellé</th>
              <th className="px-3 py-2 text-center font-semibold">Total</th>
              <th className="px-3 py-2 text-center font-semibold">Validés</th>
              <th className="px-3 py-2 text-center font-semibold">Ajournés</th>
              <th className="px-3 py-2 text-center font-semibold">Taux</th>
              <th className="px-3 py-2 text-center font-semibold">Moy.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.map(d => (
              <tr key={d.label} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="px-3 py-1.5 font-bold text-gray-900 dark:text-white">{d.label}</td>
                <td className="px-3 py-1.5 text-center text-gray-700 dark:text-gray-300">{d.total}</td>
                <td className="px-3 py-1.5 text-center text-green-700 dark:text-green-400 font-semibold">{d.valides}</td>
                <td className="px-3 py-1.5 text-center text-red-700 dark:text-red-400 font-semibold">{d.ajournes}</td>
                <td className="px-3 py-1.5 text-center">
                  <span className={`font-bold ${
                    d.tauxReussite >= 70 ? 'text-green-600' :
                    d.tauxReussite >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {d.tauxReussite.toFixed(1)}%
                  </span>
                </td>
                <td className="px-3 py-1.5 text-center font-mono">
                  <span className={d.moyenne >= 10 ? 'text-green-600' : 'text-red-600'}>
                    {d.moyenne.toFixed(2)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const KPI: React.FC<{ label: string; value: string | number; sub?: string; icon: React.ReactNode; color: string }> = ({ label, value, sub, icon, color }) => {
  const colorMap: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50 dark:bg-blue-900/15 text-blue-700 dark:text-blue-300',
    green: 'border-green-200 bg-green-50 dark:bg-green-900/15 text-green-700 dark:text-green-300',
    red: 'border-red-200 bg-red-50 dark:bg-red-900/15 text-red-700 dark:text-red-300',
    purple: 'border-purple-200 bg-purple-50 dark:bg-purple-900/15 text-purple-700 dark:text-purple-300',
    amber: 'border-amber-200 bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-300',
    emerald: 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-300',
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || colorMap.blue}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider opacity-80 truncate">{label}</div>
          <div className="text-xl font-bold mt-0.5 tabular-nums">{value}</div>
          {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
        </div>
        <div className="opacity-50">{icon}</div>
      </div>
    </div>
  );
};

export default TableauDeliberation;
